/**
 * Upscaler service for handling image upscaling API calls
 */

import API_CONFIG from "../apiConfig";

// Types for Upscaler API Response
export interface UpscalerResponse {
  userId: string;
  imageIds: string[];
  imageUrls: string[];
  method: string;
  inputImageUrls: string[];
  imageFormat: string;
  creditCost: number;
  upscaleFactor: string;
  statusCode: number;
  message: string;
  success: boolean;
}

export interface UpscalerRequest {
  imageUrl: string;
  userId: string;
  upscaleFactor?: string;
  method?: string;
  targetResolution?: string;
}

/**
 * Upscale an image using the upscaler service
 * @param request The upscaler request parameters
 * @returns Promise with the upscaler response
 */
export const upscaleImage = async (request: UpscalerRequest): Promise<UpscalerResponse> => {
  try {
    const response = await fetch(API_CONFIG.UPSCALER_GENERATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageUrl: request.imageUrl,
        userId: request.userId,
        upscaleFactor: request.upscaleFactor || '2x',
        method: request.method || 'High Fidelity V2',
        targetResolution: request.targetResolution || '2160p'
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Upscaling failed');
    }

    return data;
  } catch (error) {
    console.error('Upscaling failed:', error);
    throw error;
  }
};

/**
 * Upscale an image and open it in a new tab
 * @param imageUrl The URL of the image to upscale
 * @param userId The user ID
 * @param refreshCredits Function to refresh user credits after successful upscaling
 * @returns Promise that resolves when upscaling is complete
 */
export const upscaleImageAndOpen = async (
  imageUrl: string, 
  userId: string, 
  refreshCredits: () => Promise<void>
): Promise<void> => {
  try {
    const data = await upscaleImage({ imageUrl, userId });
    
    if (data.imageUrls && data.imageUrls.length > 0) {
      window.open(data.imageUrls[0], '_blank');
      // Refresh user credits after successful upscaling
      await refreshCredits();
    } else {
      throw new Error('No upscaled image URL returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Upscaling failed:', error);
    alert(`Upscaling failed: ${errorMessage}`);
  }
};

/**
 * Upscale an image and return the URL
 * @param imageUrl The URL of the image to upscale
 * @param userId The user ID
 * @param refreshCredits Function to refresh user credits after successful upscaling
 * @param upscaleFactor The upscale factor (e.g., '4x', '8x')
 * @param targetResolution The target resolution (e.g., '2160p', '4320p')
 * @returns Promise that resolves with the upscaled image URL
 */
export const upscaleImageAndReturn = async (
  imageUrl: string, 
  userId: string, 
  refreshCredits: () => Promise<void>,
  upscaleFactor?: string,
  targetResolution?: string
): Promise<string> => {
  try {
    const data = await upscaleImage({ imageUrl, userId, upscaleFactor, targetResolution });
    console.log('upscaled image data', data);
    
    if (data.imageUrls && data.imageUrls.length > 0) {
      // Refresh user credits after successful upscaling
      await refreshCredits();
      return data.imageUrls[0];
    } else {
      throw new Error('No upscaled image URL returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Upscaling failed:', error);
    throw new Error(`Upscaling failed: ${errorMessage}`);
  }
}; 